import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Package,
  DollarSign,
  Calendar,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  ArrowUp,
  ArrowDown,
  Minus,
  ChevronDown,
  ChevronUp,
  Check,
  BarChart3,
  Target,
  Info,
  Settings,
} from "lucide-react";
import { cn } from "../../utils/utils";
import { ColorRing } from "react-loader-spinner";
import { useGetPcpDashboardMutation } from "../../redux/slices/apiSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setIsPreFiltered, setPreDefinedFilter } from "../../redux/slices/pricingSlice";
import DUMMY_DASHBOARD_DATA from "./dummyData";
import InventoryTreemap from "./InventoryTreemap";

// ─── Toggle this flag to preview with Figma dummy data ────────────────────────
const USE_DUMMY_DATA = false;

// ─── Bucket color palettes (10 slots: green → red) ───────────────────────────
// chart fill / accent color (vibrant)
const BUCKET_PALETTE = [
  "#10b981", // 0 – A  emerald
  "#3b82f6", // 1 – B  blue
  "#84cc16", // 2 –    lime (extra)
  "#eab308", // 3 – C+ yellow
  "#f59e0b", // 4 – C- amber
  "#fb923c", // 5 –    orange-400 (extra)
  "#f97316", // 6 – D  orange
  "#ef4444", // 7 – E+ red
  "#dc2626", // 8 –    red-600 (extra)
  "#b91c1c", // 9 – E- darkest red
];
// badge background (matches Figma Tailwind *-100)
const BUCKET_BG_PALETTE = [
  "#dcfce7", "#dbeafe", "#f7fee7", "#fef9c3",
  "#fef3c7", "#fff7ed", "#ffedd5", "#fee2e2",
  "#fef2f2", "#fee2e2",
];
// badge text (matches Figma Tailwind *-800)
const BUCKET_TEXT_PALETTE = [
  "#166534", "#1e40af", "#3f6212", "#854d0e",
  "#78350f", "#7c2d12", "#9a3412", "#991b1b",
  "#7f1d1d", "#7f1d1d",
];
// badge border (matches Figma Tailwind *-300)
const BUCKET_BORDER_PALETTE = [
  "#86efac", "#93c5fd", "#bef264", "#fde047",
  "#fcd34d", "#fdba74", "#fdba74", "#fca5a5",
  "#fca5a5", "#fca5a5",
];

function buildBucketMeta(bucketConfig = []) {
  const n = bucketConfig.length;
  const map = new Map();
  bucketConfig.forEach((b, i) => {
    const idx =
      n <= 1 ? 0 : Math.floor((i * (BUCKET_PALETTE.length - 1)) / (n - 1));
    map.set(b.bucket, {
      color: BUCKET_PALETTE[idx],
      bg: BUCKET_BG_PALETTE[idx],
      textColor: BUCKET_TEXT_PALETTE[idx],
      borderColor: BUCKET_BORDER_PALETTE[idx],
      order: i,
    });
  });
  return map;
}

function getBucketColor(bucket, bucketMeta) {
  if (!bucket) return "#9ca3af";
  return bucketMeta?.get(bucket)?.color ?? "#9ca3af";
}

function getBucketStyle(bucket, bucketMeta) {
  const m = bucketMeta?.get(bucket);
  if (!m) return { bg: "#f3f4f6", textColor: "#1f2937", borderColor: "#d1d5db", color: "#9ca3af" };
  return m;
}

// ─── Formatters ───────────────────────────────────────────────────────────────
function fmtMoney(val) {
  if (val == null) return "–";
  const abs = Math.abs(val);
  if (abs >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(val / 1_000).toFixed(1)}K`;
  return `$${val.toLocaleString()}`;
}
function fmtNum(val, dec = 1) {
  if (val == null) return "–";
  return Number(val).toFixed(dec);
}
function cleanLabel(s) {
  if (!s) return "";
  return s.replace(/^\d+/, "").toUpperCase();
}
function formatKmValue(item) {
  const label = (item.label ?? "").toLowerCase();
  if (item.value == null) return "–";
  if (label.includes("aging") || label.includes("days"))
    return `${Math.round(item.value)} days`;
  if (label.includes("turn")) return fmtNum(item.value);
  if (label.includes("sales") || label.includes("inventory"))
    return fmtMoney(item.value);
  return item.value?.toLocaleString() ?? "–";
}

// Compute absolute change — uses same M/K unit scale as the raw value
function fmtAbsChange(rawValue, delta, isMoney, isAging) {
  if (rawValue == null || delta == null) return null;
  const change = (rawValue * (delta / 100)) / (1 + delta / 100);
  const sign = change >= 0 ? "+" : "";
  if (isMoney) {
    const absRaw = Math.abs(rawValue);
    if (absRaw >= 1_000_000)
      return `(${sign}$${(change / 1_000_000).toFixed(1)}M)`;
    if (absRaw >= 1_000) return `(${sign}$${(change / 1_000).toFixed(1)}K)`;
    return `(${sign}$${Math.round(change)})`;
  }
  if (isAging) return `(${sign}${Math.round(change)})`;
  return `(${sign}${Number(change).toFixed(1)})`;
}

// ─── DeltaBadge ───────────────────────────────────────────────────────────────
function DeltaBadge({ delta, isAging = false, size = "normal" }) {
  if (delta == null) return null;
  const positive = delta > 0;
  const good = isAging ? !positive : positive;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 font-bold",
        size === "small" ? "text-[11px]" : "text-[13px]",
        good ? "text-emerald-600" : "text-red-500",
      )}
    >
      <Icon className={size === "small" ? "w-3 h-3" : "w-3.5 h-3.5"} />
      {Math.abs(delta).toFixed(1)}%
    </span>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({
  label,
  value,
  delta,
  deltaLabel,
  isAging,
  icon: Icon,
  accent,
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-5 pt-4 pb-5 flex-1 min-w-0 shadow-sm flex flex-col">
      <p className="text-[12px] font-semibold text-gray-900 mb-3">{label}</p>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[28px] font-extrabold text-gray-900 leading-none truncate">
            {value}
          </p>
          <div className="h-5 flex items-center mt-2">
            {delta != null ? (
              <div className="flex items-center gap-1">
                <DeltaBadge delta={delta} isAging={isAging} />
                <span className="text-[12px] text-gray-500">{deltaLabel}</span>
              </div>
            ) : (
              <span className="text-[11px] text-gray-300">–</span>
            )}
          </div>
        </div>
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: accent + "18" }}
        >
          {Icon && <Icon className="w-5 h-5" style={{ color: accent }} />}
        </div>
      </div>
    </div>
  );
}

// ─── Bucket Label — inline pill badge (matches BucketComparisonTable reference)
function BucketLabel({ bucket, bucketMeta, size = "md" }) {
  const { bg, textColor, borderColor } = getBucketStyle(bucket, bucketMeta);
  return (
    <span
      className={cn(
        "rounded-md border font-bold inline-flex items-center flex-shrink-0",
        size === "sm" ? "px-2 py-0.5" : "px-3 py-1",
      )}
      style={{
        fontSize: size === "sm" ? "12px" : "14px",
        backgroundColor: bg,
        borderColor: borderColor,
        color: textColor,
      }}
    >
      {bucket?.toUpperCase() ?? "?"}
    </span>
  );
}

// ─── Table metric cell ────────────────────────────────────────────────────────
function TCell({ value, rawValue, delta, isAging, isMoney, className }) {
  const absChange = fmtAbsChange(rawValue, delta, isMoney, isAging);
  const positive = delta != null ? delta > 0 : null;
  const good = delta != null ? (isAging ? !positive : positive) : null;
  const arrowColor = delta == null ? "#9ca3af" : good ? "#16a34a" : "#dc2626";
  const TrendIcon = delta == null ? Minus : positive ? ArrowUp : ArrowDown;

  return (
    <td
      className={cn("p-3 text-right align-middle", className)}
      style={{ fontSize: "13px" }}
    >
      {/* Line 1: value + straight arrow (matches BucketComparisonTable reference) */}
      <div className="flex items-center justify-end gap-1.5">
        <span
          className="font-semibold text-gray-800"
          style={{ fontSize: "14px" }}
        >
          {value}
        </span>
        <TrendIcon
          className="w-4 h-4 flex-shrink-0"
          style={{ color: arrowColor }}
        />
      </div>
      {/* Line 2: percentage + absolute change */}
      {delta != null ? (
        <div
          className="mt-0.5 flex items-center justify-end gap-1"
          style={{ fontSize: "12px" }}
        >
          <span className="font-normal" style={{ color: arrowColor }}>
            {delta > 0 ? "+" : ""}
            {delta.toFixed(1)}%
          </span>
          {absChange && <span className="text-gray-400">{absChange}</span>}
        </div>
      ) : (
        <div className="h-4" />
      )}
    </td>
  );
}

// ─── Pie label inside slices ──────────────────────────────────────────────────
const RADIAN = Math.PI / 180;
function PieSliceLabel({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}) {
  if (percent < 0.05) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.52;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize: 11, fontWeight: 700 }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

// ─── Chart Tooltip ────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label, isMoney, isInteger }) {
  if (!active || !payload?.length) return null;
  const fmt = (v) => {
    if (isMoney) return fmtMoney(v);
    if (isInteger) return Math.round(v).toString();
    return fmtNum(v);
  };
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-600 mb-0.5">
        {label ?? payload[0]?.payload?.bucket ?? payload[0]?.name}
      </p>
      {payload.map((p, i) => (
        <p
          key={i}
          className="font-bold"
          style={{ color: p.color ?? "#374151" }}
        >
          {payload.length > 1 ? `${p.name}: ` : ""}
          {fmt(p.value)}
        </p>
      ))}
    </div>
  );
}

// ─── Empty Chart ──────────────────────────────────────────────────────────────
function EmptyChart({ loading }) {
  return (
    <div className="h-[220px] flex items-center justify-center text-gray-300 text-xs border border-dashed border-gray-200 rounded-lg">
      {loading ? "Loading…" : "No data available"}
    </div>
  );
}

// ─── Panel Row ────────────────────────────────────────────────────────────────
function PanelRow({ label, bucket, value, color, bucketMeta, arrow }) {
  const bStyle = bucket ? getBucketStyle(bucket, bucketMeta) : null;
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
      <div className="flex items-center gap-2 min-w-0">
        {bucket ? (
          <span
            className="px-2 py-0.5 rounded-md border font-semibold flex-shrink-0"
            style={{
              fontSize: "11px",
              backgroundColor: bStyle?.bg ?? "#f3f4f6",
              borderColor: bStyle?.borderColor ?? "#d1d5db",
              color: bStyle?.textColor ?? "#1f2937",
            }}
          >
            {bucket.toUpperCase()}
          </span>
        ) : (
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: color || "#d1d5db" }}
          />
        )}
        <span className="text-[13px] text-gray-600 font-medium truncate">
          {label}
        </span>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
        <span className="text-[14px] font-bold text-gray-800">
          {value ?? "–"}
        </span>
        {/*{arrow === "up" && (
          <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" />
        )}
        {arrow === "down" && (
          <ArrowDownRight className="w-3.5 h-3.5 text-red-500" />
        )}*/}
      </div>
    </div>
  );
}

// ─── Category Card ────────────────────────────────────────────────────────────
function CategoryCard({ item, rank, isOutperforming, bucketMeta }) {
  const [shape = "", carat = "", color = "", clarity = ""] = (
    item.bucket_key ?? ""
  ).split("|");
  const accentColor = isOutperforming ? "#22c55e" : "#ef4444";
  const needsColor = isOutperforming
    ? { bg: "#dcfce7", text: "#15803d" }
    : { bg: "#fee2e2", text: "#dc2626" };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
      <div className="h-[3px]" style={{ backgroundColor: accentColor }} />
      <div className="p-3.5 flex flex-col gap-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
              style={{ backgroundColor: accentColor }}
            >
              #{rank}
            </span>
            <span className="font-bold text-[13px] text-gray-800 truncate">
              {cleanLabel(shape) || "–"}
            </span>
            {item.bucket && (() => {
              const bs = getBucketStyle(item.bucket, bucketMeta);
              return (
                <span
                  className="text-[9px] font-bold px-1.5 py-0.5 rounded-md border uppercase flex-shrink-0"
                  style={{ backgroundColor: bs.bg, borderColor: bs.borderColor, color: bs.textColor }}
                >
                  {item.bucket}
                </span>
              );
            })()}
          </div>
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: needsColor.bg, color: needsColor.text }}
          >
            {item.needs > 0 ? "+" : ""}
            {item.needs}
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          {[carat, cleanLabel(color), cleanLabel(clarity)]
            .filter(Boolean)
            .map((t, i) => (
              <span
                key={i}
                className="text-[10px] bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded font-medium text-gray-600"
              >
                {t}
              </span>
            ))}
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
          <div>
            <p className="text-[9px] text-gray-400 uppercase tracking-wide mb-0.5">
              Inv Value
            </p>
            <p className="text-[13px] font-bold text-gray-800">
              {fmtMoney(item.inv_value)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[9px] text-gray-400 uppercase tracking-wide mb-0.5">
              Turn Rate
            </p>
            <p className="text-[13px] font-bold text-gray-800">
              {fmtNum(item.turn)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Category Section ─────────────────────────────────────────────────────────
function CategorySection({ title, subtitle, items = [], isOutperforming, bucketMeta }) {
  const Icon = isOutperforming ? TrendingUp : TrendingDown;
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Icon
          className={cn(
            "w-4 h-4",
            isOutperforming ? "text-green-500" : "text-red-500",
          )}
        />
        <div>
          <h3 className="text-sm font-bold text-gray-800">{title}</h3>
          <p className="text-[11px] text-gray-400">{subtitle}</p>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-gray-300 py-6 text-center border border-dashed border-gray-200 rounded-lg">
          No data
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.slice(0, 5).map((item, i) => (
            <CategoryCard
              key={item.bucket_key ?? i}
              item={item}
              rank={i + 1}
              isOutperforming={isOutperforming}
              bucketMeta={bucketMeta}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const PCPDashboard = ({ accountDetails }) => {
  const accountId = accountDetails?.account_id;
  const isDiacrown = String(accountId) === "552" || String(accountId) === "1130";

  // ── State
  const [comparisonPeriod, setComparisonPeriod] = useState("last_week");
  const [activeTab, setActiveTab] = useState("bucket");
  const [showBucketCriteria, setShowBucketCriteria] = useState(false);
  const [dashData, setDashData] = useState(
    USE_DUMMY_DATA ? DUMMY_DASHBOARD_DATA : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const [highlightCriteria, setHighlightCriteria] = useState(false);
  const criteriaRef = useRef(null);

  const [getPcpDashboard] = useGetPcpDashboardMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleBucketClick = useCallback((bucket) => {
    const filter = {
      item_filter: { from: {}, to: {}, discrete: {}, text: {} },
      market_filters: { "stock_performance_data.bucket": [bucket] },
    };
    dispatch(setIsPreFiltered(true));
    dispatch(setPreDefinedFilter(filter));
    navigate("/price_co-pilot?page=0");
  }, [dispatch, navigate]);

  // ── Fetch (skipped when USE_DUMMY_DATA is true)
  const fetchData = useCallback(async () => {
    if (USE_DUMMY_DATA) {
      setDashData(DUMMY_DASHBOARD_DATA);
      return;
    }
    if (!accountId) return;
    setIsLoading(true);
    setError(null);
    try {
      const body = {
        seller_account_id: accountId,
        compare_with: comparisonPeriod,
      };
      const result = await getPcpDashboard(body).unwrap();
      setDashData(result?.data ?? null);
    } catch (err) {
      console.error("PCP Dashboard error:", err);
      const status = err?.status ?? err?.response?.status;
      if (status === 401 || status === 403) {
        setError("Session expired or unauthorized. Please log in again.");
      } else if (status === 404) {
        setError("Dashboard data not found for this account.");
      } else if (status === 500) {
        setError("Server error. Please try again later.");
      } else if (err?.name === "TypeError" || status === "FETCH_ERROR") {
        setError("Network error. Please check your connection.");
      } else {
        setError("Failed to load dashboard data. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [accountId, comparisonPeriod, getPcpDashboard]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Derived data
  const overall = dashData?.overall ?? {};
  const bucketPerf = dashData?.bucket_performance ?? [];
  const categoryPerf = dashData?.category_performance ?? {};
  const visual = dashData?.visual_analytics ?? {};
  const keyMetrics = dashData?.key_metrics ?? {};
  const bucketConfig = dashData?.bucket_config ?? [];
  const usesProjTurn = bucketConfig.some((c) => c.current_turn_filter != null);
  const turnKey = usesProjTurn ? "current_turn_filter" : "turn_filter";
  const turnLabel = usesProjTurn ? "Proj Turn" : "Turn";
  const bucketMeta = buildBucketMeta(bucketConfig);

  const sortedBuckets = [...bucketPerf].sort((a, b) => {
    const ai = bucketMeta.get(a.bucket)?.order ?? 999;
    const bi = bucketMeta.get(b.bucket)?.order ?? 999;
    return ai - bi;
  });

  const mkChartData = (arr) =>
    (arr ?? []).map((d) => ({
      ...d,
      color: getBucketColor(d.bucket, bucketMeta),
    }));

  const invByBucket = mkChartData(visual.inv_by_bucket).filter(
    (d) => d.value != null && d.value > 0,
  );
  const salesByBucket = mkChartData(visual.sales_by_bucket).filter(
    (d) => d.value != null && d.value > 0,
  );
  // Filter null turns so there are no empty gaps in the bar chart
  const turnByBucket = mkChartData(visual.turn_by_bucket).filter(
    (d) => d.turn != null,
  );
  const agingByBucket = mkChartData(visual.aging_by_bucket);

  const COMPARE_OPTIONS = [
    { value: "last_week",     label: "Previous Week" },
    { value: "last_month",    label: "Previous Month" },
    { value: "last_quarter",  label: "Previous Quarter" },
    { value: "last_6_months", label: "Previous 6 Months" },
    { value: "last_year",     label: "Previous Year" },
  ];

  const selectedOption = COMPARE_OPTIONS.find(o => o.value === comparisonPeriod) ?? COMPARE_OPTIONS[0];
  const deltaLabel = "vs " + selectedOption.label;

  const TABS = [
    { id: "bucket", label: "Bucket Performance", Icon: TrendingUp },
    { id: "pricing", label: "Pricing Actions", Icon: Activity },
    { id: "category", label: "Category Analysis", Icon: Target },
  ];

  const legendStyle = {
    wrapperStyle: { paddingTop: 10 },
    iconSize: 10,
    formatter: (value) => (
      <span style={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>
        {value}
      </span>
    ),
  };

  // ── Full-page loader (first load only)
  if (isLoading && !dashData) {
    return (
      <div className="flex justify-center items-center h-full w-full">
        <ColorRing
          visible={true}
          height="60"
          width="60"
          ariaLabel="blocks-loading"
          colors={["#00319f", "#2e56b1", "#5c7bc2", "#8aa1d3", "#b2c1e2"]}
        />
      </div>
    );
  }

  // ── Full-page error (no data at all)
  if (error && !dashData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <div className="bg-red-50 border border-red-200 rounded-2xl px-8 py-6 max-w-md text-center shadow-sm">
          <p className="text-base font-semibold text-red-600 mb-1">Something went wrong</p>
          <p className="text-sm text-red-500 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-[#4154f1] text-white text-sm font-semibold rounded-lg hover:bg-[#3344d0] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Render
  return (
    <div className="min-h-screen mx-auto max-w-[1600px] px-8 py-5">
      {/* ══════════ ERROR (inline — shown when refresh fails but old data still visible) ══════════ */}
      {error && dashData && (
        <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl px-4 py-3 mb-5 font-medium flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="text-xs underline ml-4 font-semibold">Retry</button>
        </div>
      )}

      {/* ══════════ KPI STRIP ══════════ */}
      <div className="grid grid-cols-2 [@media(min-width:1340px)]:grid-cols-4 gap-4 mb-5">
        <KPICard
          label="Total Inventory"
          value={isLoading ? "–" : fmtMoney(overall.inv_value)}
          delta={overall.delta?.inv_value}
          deltaLabel={deltaLabel}
          icon={Package}
          accent="#4154f1"
        />
        <KPICard
          label="Total Sales"
          value={isLoading ? "–" : fmtMoney(overall.sold_value)}
          delta={overall.delta?.sold_value}
          deltaLabel={deltaLabel}
          icon={DollarSign}
          accent="#22c55e"
        />
        <KPICard
          label="Avg Turnover"
          value={isLoading ? "–" : fmtNum(overall.turn, 2)}
          delta={overall.delta?.turn}
          deltaLabel={deltaLabel}
          icon={RefreshCw}
          accent="#f97316"
        />
        <KPICard
          label="Avg Inv Aging"
          value={
            isLoading
              ? "–"
              : overall.inv_aging != null
                ? `${fmtNum(overall.inv_aging, 0)} days`
                : "–"
          }
          delta={overall.delta?.inv_aging}
          deltaLabel={deltaLabel}
          isAging
          icon={Calendar}
          accent="#ef4444"
        />
      </div>

      {/* ══════════ TABS ══════════ */}
      {/* hidden — defaulting to Bucket Performance */}
      {/* <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1">
        {TABS.map((t) => (
          <div
            key={t.id}
            tabIndex={0}
            onClick={() => setActiveTab(t.id)}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 px-5 py-2.5 text-[13px] font-semibold rounded-lg transition-all cursor-pointer select-none",
              activeTab === t.id
                ? "bg-white shadow-sm text-[#4154f1]"
                : "text-gray-500 hover:text-gray-700",
            )}
          >
            <t.Icon className="w-4 h-4" />
            {t.label}
          </div>
        ))}
      </div> */}

      {/* ══════════ BUCKET PERFORMANCE TAB ══════════ */}
      {activeTab === "bucket" && (
        <div className="flex flex-col gap-5">
          {/* ── Bucket Table ── */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-2">
            <div className="flex items-start justify-between py-3 border-b border-gray-100 gap-4 flex-wrap">
              <div>
                <h2 className="text-[17px] font-semibold text-gray-900">
                  Bucket Performance Comparison
                </h2>
                <p className="text-[12px] text-gray-500 mt-0.5">
                  Compare current metrics with{" "}
                  {selectedOption.label.toLowerCase()}{" "}
                  data.{" "}
                  <span className="text-emerald-600 font-semibold">Green</span>{" "}
                  indicates improvement,{" "}
                  <span className="text-red-600 font-semibold">red</span>{" "}
                  indicates decline.
                </p>
              </div>
              <div className="flex items-center gap-2.5 flex-shrink-0">
                {/* Compare dropdown */}
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-gray-500 font-medium whitespace-nowrap">
                    Compare:
                  </span>
                  <div className="relative">
                    <div
                      tabIndex={0}
                      onClick={() => setSelectOpen((v) => !v)}
                      className="flex items-center justify-between gap-2 pl-3 pr-2.5 py-1.5 min-w-[140px] bg-white border border-gray-300 rounded-lg text-[12px] font-semibold text-gray-700 cursor-pointer hover:bg-gray-50 select-none"
                    >
                      <span>{selectedOption.label}</span>
                      <ChevronDown
                        className={cn(
                          "w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-150",
                          selectOpen && "rotate-180",
                        )}
                      />
                    </div>
                    {selectOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setSelectOpen(false)}
                        />
                        <div className="absolute right-0 mt-1 z-20 min-w-[150px] bg-white border border-gray-200 rounded-xl shadow-md overflow-hidden p-1">
                          {COMPARE_OPTIONS.map((opt) => {
                            const active = comparisonPeriod === opt.value;
                            return (
                              <div
                                key={opt.value}
                                onClick={() => {
                                  setComparisonPeriod(opt.value);
                                  setSelectOpen(false);
                                }}
                                className={cn(
                                  "relative flex items-center pl-2 pr-8 py-1.5 rounded-md text-[12px] cursor-pointer select-none transition-colors whitespace-nowrap",
                                  active
                                    ? "bg-gray-100 text-gray-900 font-semibold"
                                    : "text-gray-700 font-normal hover:bg-gray-100",
                                )}
                              >
                                {opt.label}
                                {active && (
                                  <span className="absolute right-2">
                                    <Check className="w-3.5 h-3.5 text-[#4154f1]" />
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* Bucket Criteria toggle */}
                <button
                  onClick={() => {
                    const willOpen = !showBucketCriteria;
                    setShowBucketCriteria(willOpen);
                    if (willOpen) {
                      setTimeout(() => {
                        criteriaRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "nearest",
                        });
                        setHighlightCriteria(true);
                        setTimeout(() => setHighlightCriteria(false), 1200);
                      }, 80);
                    }
                  }}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 text-[12px] font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Settings className="w-3.5 h-3.5 text-gray-500" />
                  Bucket Criteria
                  {showBucketCriteria ? (
                    <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                  )}
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table
                className="w-full"
                style={{ borderCollapse: "collapse", fontSize: "14px" }}
              >
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    {[
                      { label: "Bucket", sub: null, align: "left" },
                      { label: "Inventory", sub: deltaLabel, align: "right" },
                      { label: "Sales", sub: deltaLabel, align: "right" },
                      { label: "Turn", sub: deltaLabel, align: "right" },
                      { label: "Inv Aging", sub: deltaLabel, align: "right" },
                      { label: "Sales Aging", sub: deltaLabel, align: "right" },
                    ].map(({ label, sub, align }, idx, arr) => (
                      <th
                        key={label}
                        className={cn(
                          align === "left"
                            ? "p-3 pl-5 bg-gray-50 text-left"
                            : "p-3 bg-gray-50 text-right",
                          idx === arr.length - 1 && "pr-5",
                        )}
                        style={{ fontSize: "14px", color: "#0A0A0A" }}
                      >
                        <div className="font-bold">{label}</div>
                        {sub && (
                          <div
                            className="font-normal text-gray-500 mt-0.5"
                            style={{ fontSize: "11px" }}
                          >
                            {sub}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-12 text-center text-gray-300"
                        style={{ fontSize: "13px" }}
                      >
                        Loading data…
                      </td>
                    </tr>
                  ) : sortedBuckets.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-12 text-center text-gray-300"
                        style={{ fontSize: "13px" }}
                      >
                        No bucket data available
                      </td>
                    </tr>
                  ) : (
                    sortedBuckets.map((row, rowIdx) => {
                      const color = getBucketColor(row.bucket, bucketMeta);
                      const cfg = bucketConfig.find(
                        (c) => c.bucket === row.bucket,
                      );
                      const tooltipAbove =
                        rowIdx >= Math.ceil(sortedBuckets.length / 2);
                      return (
                        <tr
                          key={row.bucket}
                          className="border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-colors"
                        >
                          <td className="p-3 pl-5" style={{ fontSize: "13px" }}>
                            <div
                              className={cn("relative group/bkt inline-block", !isDiacrown && "cursor-pointer")}
                              onClick={!isDiacrown ? () => handleBucketClick(row.bucket) : undefined}
                            >
                              <BucketLabel bucket={row.bucket} bucketMeta={bucketMeta} />
                              {cfg && (
                                <div
                                  className={cn(
                                    "pointer-events-none absolute left-0 z-50 hidden group-hover/bkt:block w-52 bg-white border border-gray-200 rounded-xl shadow-xl p-3 text-left",
                                    tooltipAbove
                                      ? "bottom-full mb-1.5"
                                      : "top-full mt-1.5",
                                  )}
                                >
                                  <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">
                                    Bucket {row.bucket} Criteria
                                  </p>
                                  {[
                                    [turnLabel, cfg[turnKey]],
                                    ["Sold Aging", cfg.sold_aging_filter],
                                    ["Sold Count", cfg.sold_count_filter],
                                    ["Inv Aging", cfg.inv_aging_filter],
                                    ["Inv Count", cfg.inv_count_filter],
                                  ]
                                    .filter(([, v]) => v)
                                    .map(([k, v]) => (
                                      <div
                                        key={k}
                                        className="flex items-center justify-between py-0.5"
                                      >
                                        <span className="text-[11px] text-gray-500">
                                          {k}
                                        </span>
                                        <span
                                          className="text-[11px] font-semibold"
                                          style={{ color }}
                                        >
                                          {usesProjTurn ? String(v).replace(/Current Turn/gi, "Proj Turn") : v}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <TCell
                            value={fmtMoney(row.inv_value)}
                            rawValue={row.inv_value}
                            delta={row.delta?.inv_value}
                            isMoney
                          />
                          <TCell
                            value={fmtMoney(row.sales_value)}
                            rawValue={row.sales_value}
                            delta={row.delta?.sales_value}
                            isMoney
                          />
                          <TCell
                            value={fmtNum(row.turn)}
                            rawValue={row.turn}
                            delta={row.delta?.turn}
                          />
                          <TCell
                            value={
                              row.inv_aging != null
                                ? fmtNum(row.inv_aging, 0)
                                : "–"
                            }
                            rawValue={row.inv_aging}
                            delta={row.delta?.inv_aging}
                            isAging
                          />
                          <TCell
                            value={
                              row.sales_aging != null
                                ? fmtNum(row.sales_aging, 0)
                                : "–"
                            }
                            rawValue={row.sales_aging}
                            delta={row.delta?.sales_aging}
                            isAging
                            className="pr-5"
                          />
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Bucket Classification Criteria — separate card ── */}
          {showBucketCriteria && (
            <div
              ref={criteriaRef}
              className={cn(
                "bg-white rounded-xl border shadow-sm transition-all duration-300 px-5 py-2",
                highlightCriteria
                  ? "border-[#4154f1] ring-2 ring-[#4154f1]/30"
                  : "border-gray-200",
              )}
            >
              {/* Header — flex + leading-none on text prevents misalignment */}
              <div className="flex items-start gap-2 py-3 border-b border-gray-200">
                <Info className="w-[18px] h-[18px] text-[#4154f1] flex-shrink-0 mt-0.5" />
                <div>
                  <span
                    className="font-semibold text-gray-900 leading-none"
                    style={{ fontSize: "17px" }}
                  >
                    Bucket Classification Criteria
                  </span>
                  <p className="text-[12px] text-gray-500 mt-0.5">
                    Rules used to classify inventory into buckets. Each bucket
                    is defined by a combination of{" "}
                    <span className="font-semibold text-gray-700">
                      turn rate
                    </span>
                    , <span className="font-semibold text-gray-700">aging</span>
                    , and{" "}
                    <span className="font-semibold text-gray-700">count</span>{" "}
                    thresholds.
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table
                  className="w-full"
                  style={{
                    borderCollapse: "collapse",
                    fontSize: "13px",
                    width: "100%",
                  }}
                >
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      {[
                        "Bucket",
                        `${turnLabel} Filter`,
                        "Sales Aging Filter",
                        "Sales Count Filter",
                        "Inv Aging Filter",
                        "Inv Count Filter",
                      ].map((h, idx, arr) => (
                        <th
                          key={h}
                          className={cn(
                            "px-3 py-2 text-left bg-gray-50 text-gray-700",
                            idx === 0 && "pl-5",
                            idx === arr.length - 1 && "pr-5",
                          )}
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#0A0A0A",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bucketConfig.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="p-3 text-gray-300 text-center"
                          style={{ fontSize: "13px" }}
                        >
                          No criteria data
                        </td>
                      </tr>
                    ) : (
                      bucketConfig.map((cfg) => (
                        <tr
                          key={cfg.bucket}
                          className="border-b border-gray-200 last:border-0 hover:bg-gray-50/60 transition-colors"
                        >
                          <td className="px-3 py-2 pl-5">
                            <BucketLabel bucket={cfg.bucket} bucketMeta={bucketMeta} size="sm" />
                          </td>
                          {[
                            cfg[turnKey],
                            cfg.sold_aging_filter,
                            cfg.sold_count_filter,
                            cfg.inv_aging_filter,
                            cfg.inv_count_filter,
                          ].map((v, i, arr) => (
                            <td
                              key={i}
                              className={cn(
                                "px-3 py-2",
                                i === arr.length - 1 && "pr-5",
                              )}
                              style={{ fontSize: "13px", color: "#111827" }}
                            >
                              {v != null ? (usesProjTurn ? String(v).replace(/Current Turn/gi, "Proj Turn") : v) : "-"}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Visual Analytics ── */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-[#4154f1]" />
              <h2 className="text-[17px] font-semibold text-gray-900">
                Visual Analytics
              </h2>
            </div>
            <div className="flex flex-col gap-5">
            {/* ── Row 1: pie charts 50/50 ── */}
            <div className="grid grid-cols-1 [@media(min-width:1340px)]:grid-cols-2 gap-5">
              {/* Inventory Distribution */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <p
                  className="font-semibold text-gray-800 mb-4"
                  style={{ fontSize: "15px" }}
                >
                  Inventory Distribution by Bucket
                </p>
                {invByBucket.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={invByBucket}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ bucket, percent, x, y, textAnchor, fill }) => {
                          if (percent < 0.04) return null;
                          return (
                            <text x={x} y={y} textAnchor={textAnchor} fill={fill} fontSize={11} alignmentBaseline="middle">
                              {`${bucket?.toUpperCase() ?? "?"}: ${(percent * 100).toFixed(0)}%`}
                            </text>
                          );
                        }}
                        outerRadius={100}
                        dataKey="value"
                        nameKey="bucket"
                      >
                        {invByBucket.map((d, i) => (
                          <Cell key={i} fill={d.color} />
                        ))}
                      </Pie>
                      <ReTooltip formatter={(v, name) => [fmtMoney(v), name?.toUpperCase?.() ?? name]} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart loading={isLoading} />
                )}
              </div>

              {/* Sales Distribution */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <p
                  className="font-semibold text-gray-800 mb-4"
                  style={{ fontSize: "15px" }}
                >
                  Sales Distribution by Bucket
                </p>
                {salesByBucket.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={salesByBucket}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ bucket, percent, x, y, textAnchor, fill }) => {
                          if (percent < 0.04) return null;
                          return (
                            <text x={x} y={y} textAnchor={textAnchor} fill={fill} fontSize={11} alignmentBaseline="middle">
                              {`${bucket?.toUpperCase() ?? "?"}: ${(percent * 100).toFixed(0)}%`}
                            </text>
                          );
                        }}
                        outerRadius={100}
                        dataKey="value"
                        nameKey="bucket"
                      >
                        {salesByBucket.map((d, i) => (
                          <Cell key={i} fill={d.color} />
                        ))}
                      </Pie>
                      <ReTooltip formatter={(v, name) => [fmtMoney(v), name?.toUpperCase?.() ?? name]} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart loading={isLoading} />
                )}
              </div>
            </div>

            {/* ── Row 2: Inventory Breakdown Treemap (full width) ── */}
            <InventoryTreemap
              accountId={accountId}
              invValue={overall.inv_value}
              avgTurn={overall.turn}
            />

            {/* ── Row 3: Turnover Rate + Aging Comparison ── */}
            <div className="grid grid-cols-1 [@media(min-width:1340px)]:grid-cols-2 gap-5">
              {/* Turnover Rate */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <p
                  className="font-semibold text-gray-800 mb-4"
                  style={{ fontSize: "15px" }}
                >
                  Turnover Rate by Bucket
                </p>
                {turnByBucket.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={turnByBucket}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 12 }} tickFormatter={(v) => v?.toUpperCase?.() ?? v} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <ReTooltip labelFormatter={(v) => v?.toUpperCase?.() ?? v} />
                      <Legend {...legendStyle} />
                      <Bar dataKey="turn" name="Turn Rate">
                        {turnByBucket.map((d, i) => (
                          <Cell key={i} fill={d.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart loading={isLoading} />
                )}
              </div>

              {/* Aging Comparison */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <p
                  className="font-semibold text-gray-800 mb-4"
                  style={{ fontSize: "15px" }}
                >
                  Aging Comparison by Bucket
                </p>
                {agingByBucket.some(
                  (d) => d.inv_aging != null || d.sales_aging != null,
                ) ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={agingByBucket}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 12 }} tickFormatter={(v) => v?.toUpperCase?.() ?? v} />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={(v) => Math.round(v)}
                      />
                      <ReTooltip formatter={(v) => Math.round(v)} labelFormatter={(v) => v?.toUpperCase?.() ?? v} />
                      <Legend {...legendStyle} />
                      <Bar
                        dataKey="inv_aging"
                        name="Inv Aging"
                        fill="#f97316"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={24}
                      />
                      <Bar
                        dataKey="sales_aging"
                        name="Sales Aging"
                        fill="#3b82f6"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={24}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart loading={isLoading} />
                )}
              </div>
            </div>
            </div>
          </div>

          {/* ── Bottom Row: Top Performers | Needs Attention | Key Metrics ── */}
          <div className="grid grid-cols-1 [@media(min-width:1340px)]:grid-cols-3 gap-5">
            {/* Top Performers */}
            <div className="bg-emerald-50 rounded-xl border border-emerald-100 shadow-sm overflow-hidden">
              <div className="flex items-center gap-2.5 px-4 py-3 border-b border-emerald-100">
                <div className="w-1 h-4 rounded-full bg-emerald-400 flex-shrink-0" />
                <TrendingUp className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span
                  className="font-bold text-gray-700 uppercase tracking-wide"
                  style={{ fontSize: "13px" }}
                >
                  Top Performers
                </span>
              </div>
              <div className="px-4 py-2">
                {(keyMetrics.top_performers ?? []).length === 0 ? (
                  <p className="text-xs text-gray-300 py-4 text-center">
                    No data
                  </p>
                ) : (
                  (keyMetrics.top_performers ?? []).map((item, i) => (
                    <PanelRow
                      key={i}
                      label={item.label}
                      bucket={item.bucket}
                      value={formatKmValue(item)}
                      color={getBucketColor(item.bucket, bucketMeta)}
                      bucketMeta={bucketMeta}
                      arrow={
                        item.label?.toLowerCase().includes("aging")
                          ? "down"
                          : "up"
                      }
                    />
                  ))
                )}
              </div>
            </div>

            {/* Needs Attention */}
            <div className="bg-red-50 rounded-xl border border-red-100 shadow-sm overflow-hidden">
              <div className="flex items-center gap-2.5 px-4 py-3 border-b border-red-100">
                <div className="w-1 h-4 rounded-full bg-red-400 flex-shrink-0" />
                <TrendingDown className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span
                  className="font-bold text-gray-700 uppercase tracking-wide"
                  style={{ fontSize: "13px" }}
                >
                  Needs Attention
                </span>
              </div>
              <div className="px-4 py-2">
                {(keyMetrics.needs_attention ?? []).length === 0 ? (
                  <p className="text-xs text-gray-300 py-4 text-center">
                    No data
                  </p>
                ) : (
                  (keyMetrics.needs_attention ?? []).map((item, i) => (
                    <PanelRow
                      key={i}
                      label={item.label}
                      bucket={item.bucket}
                      value={formatKmValue(item)}
                      color={getBucketColor(item.bucket, bucketMeta)}
                      bucketMeta={bucketMeta}
                      arrow={
                        item.label?.toLowerCase().includes("aging")
                          ? "up"
                          : "down"
                      }
                    />
                  ))
                )}
              </div>
            </div>

            {/* Key Metrics */}
            <div className="bg-blue-50 rounded-xl border border-blue-100 shadow-sm overflow-hidden">
              <div className="flex items-center gap-2.5 px-4 py-3 border-b border-blue-100">
                <div className="w-1 h-4 rounded-full bg-[#4154f1] flex-shrink-0" />
                <Activity className="w-4 h-4 text-[#4154f1] flex-shrink-0" />
                <span
                  className="font-bold text-gray-700 uppercase tracking-wide"
                  style={{ fontSize: "13px" }}
                >
                  Key Metrics
                </span>
              </div>
              <div className="px-4 py-2">
                <PanelRow
                  label="Total Inventory"
                  value={fmtMoney(keyMetrics.summary?.total_inv_value)}
                  color="#4154f1"
                />
                <PanelRow
                  label="Total Sales"
                  value={fmtMoney(keyMetrics.summary?.total_sold_value)}
                  color="#22c55e"
                />
                <PanelRow
                  label="Overall Turn"
                  value={fmtNum(keyMetrics.summary?.overall_turn, 2)}
                  color="#f97316"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ PRICING ACTIONS TAB ══════════ */}
      {activeTab === "pricing" && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex items-center justify-center py-20">
          <p className="text-sm text-gray-300 font-medium">
            Pricing Actions — coming soon
          </p>
        </div>
      )}

      {/* ══════════ CATEGORY ANALYSIS TAB ══════════ */}
      {activeTab === "category" && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="mb-5">
            <h2 className="text-[15px] font-bold text-gray-800">
              Category Performance Analysis
            </h2>
            <p className="text-[12px] text-gray-400 mt-0.5">
              Analyse performance across diamond categories with market insights
            </p>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-300 text-sm">
              Loading…
            </div>
          ) : (
            <div className="flex flex-col gap-8">
              <CategorySection
                title="Top 5 Outperforming Categories"
                subtitle="Categories where you exceed past sales"
                items={categoryPerf.top_outperforming ?? []}
                isOutperforming
                bucketMeta={bucketMeta}
              />
              <CategorySection
                title="Top 5 Underperforming Categories"
                subtitle="Categories where you are below past sales"
                items={categoryPerf.top_underperforming ?? []}
                isOutperforming={false}
                bucketMeta={bucketMeta}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PCPDashboard;
